// Water shader for PixiJS
export const waterVertexShader = `
    attribute vec2 aVertexPosition;
    attribute vec2 aUvs;
    uniform mat3 translationMatrix;
    uniform mat3 projectionMatrix;
    varying vec2 vUvs;
    varying vec2 vPixelizedUvs;

    void main() {
        vUvs = aUvs;
        vPixelizedUvs = floor(aUvs * 2048.0) / 2048.0; // pixelization
        gl_Position = vec4((projectionMatrix * translationMatrix * vec3(aVertexPosition, 1.0)).xy, 0.0, 1.0);
    }
`;

export const waterFragmentShader = `
    precision mediump float;
    varying vec2 vUvs;
    varying vec2 vPixelizedUvs;

    uniform sampler2D uTexture;
    uniform sampler2D uWaterDepthGradient;
    uniform sampler2D uCausticTexture;
    uniform sampler2D uCausticHighlightTexture;
    uniform sampler2D uCausticNoiseTexture;
    uniform sampler2D uCausticFadeNoiseTexture;
    uniform sampler2D uSpecularNoiseTexture;
    uniform sampler2D uSpecularMovementLeftNoiseTexture;
    uniform sampler2D uSpecularMovementRightNoiseTexture;
    uniform sampler2D uFoamTexture;

    uniform vec4 uCausticColor;
    uniform vec4 uCausticHighlightColor;
    uniform vec4 uSpecularColor;
    uniform vec4 uFoamColor;
    uniform vec4 uOutlineColor;

    uniform float uCausticScale;
    uniform float uCausticSpeed;
    uniform float uCausticMovementAmount;
    uniform float uCausticFaderMultiplier;
    uniform float uSpecularThreshold;
    uniform float uSpecularSpeed;
    uniform float uSpecularScale;
    uniform float uFoamIntensity;
    uniform float uFoamScale;
    uniform float uGeneralTransparency;
    uniform float time;

    // Helper functions
    vec2 blendSubtract(vec2 base, vec2 blend, float opacity) {
        vec2 result = base - blend;
        return mix(base, result, opacity);
    }

    float blendSubtract(float base, float blend, float opacity) {
        float result = base - blend;
        return mix(base, result, opacity);
    }

    float blendOverlay(float base, float blend, float opacity) {
        float result1 = 1.0 - 2.0 * (1.0 - base) * (1.0 - blend);
        float result2 = 2.0 * base * blend;
        float zeroOrOne = step(0.5, base);
        float res = result2 * zeroOrOne + (1.0 - zeroOrOne) * result1;
        return mix(base, res, opacity);
    }

    // Shader layers
    vec4 caustics(vec2 pixelizedCoordinates) {
        float currentTime = time * uCausticSpeed;
        vec4 causticNoise = texture2D(uCausticNoiseTexture, pixelizedCoordinates + vec2(currentTime, 0.0));
        vec2 noiseCoordinates = blendSubtract(pixelizedCoordinates * uCausticScale, causticNoise.rg, uCausticMovementAmount);
        vec4 causticHighlight = texture2D(uCausticHighlightTexture, noiseCoordinates) * uCausticHighlightColor;
        vec4 caustic = texture2D(uCausticTexture, noiseCoordinates) * uCausticColor;
        vec4 interpolatedCaustics = mix(caustic, causticHighlight, causticHighlight.a);
        float fadeNoise = texture2D(uCausticFadeNoiseTexture, noiseCoordinates).r * uCausticFaderMultiplier;
        return vec4(interpolatedCaustics.rgb, clamp(interpolatedCaustics.a - fadeNoise, 0.0, 1.0));
    }

    vec4 specular(vec2 pixelizedCoordinates) {
        float currentTime = time * uSpecularSpeed;
        vec2 scaledCoordinates = pixelizedCoordinates * uSpecularScale;
        float specularNoise = texture2D(uSpecularNoiseTexture, scaledCoordinates).r;
        float leftScrollingNoise = texture2D(uSpecularMovementLeftNoiseTexture, scaledCoordinates + vec2(currentTime, 0.0)).r;
        float rightScrollingNoise = texture2D(uSpecularMovementRightNoiseTexture, scaledCoordinates + vec2(-currentTime, 0.0)).r;
        return step(uSpecularThreshold, blendSubtract(blendOverlay(leftScrollingNoise, rightScrollingNoise, 1.0), specularNoise, 1.0)) * uSpecularColor;
    }

    vec4 foam(vec2 pixelizedCoordinates, vec4 mainTexColor) {
        vec4 colorizedFoam = texture2D(uFoamTexture, pixelizedCoordinates * uFoamScale) * uFoamColor;
        float intensity = clamp(mainTexColor.g * mainTexColor.a - uFoamIntensity, 0.0, 1.0);
        return vec4(colorizedFoam.rgb, colorizedFoam.a * intensity);
    }

    void main() {
        vec4 mainTex = texture2D(uTexture, vUvs);
        vec4 depthBasedWaterColor = texture2D(uWaterDepthGradient, vec2(1.0 - mainTex.b, 1.0));
        
        vec4 finalCaustics = caustics(vPixelizedUvs);
        vec4 finalSpecular = specular(vPixelizedUvs);
        vec4 finalFoam = foam(vPixelizedUvs, mainTex);
        
        vec4 waterWithCausticLayer = mix(depthBasedWaterColor, finalCaustics, finalCaustics.a);
        vec4 waterWithCausticAndSpecularLayer = mix(waterWithCausticLayer, finalSpecular, ceil(finalCaustics.a) * finalSpecular.a);
        vec4 waterWithCausticAndSpecularAndFoamLayer = mix(waterWithCausticAndSpecularLayer, finalFoam, finalFoam.a);
        
        float outline = mainTex.a * mainTex.r;
        vec4 finalOutlineColor = outline * uOutlineColor;
        
        vec4 finalRGBColor = mix(waterWithCausticAndSpecularAndFoamLayer, finalOutlineColor, outline);
        gl_FragColor = vec4(finalRGBColor.rgb, mainTex.b * uGeneralTransparency);
    }
`;

// Default uniforms for the water shader
export const waterShaderUniforms = {
    uWaterDepthGradient: null,
    uCausticTexture: null,
    uCausticHighlightTexture: null,
    uCausticNoiseTexture: null,
    uCausticFadeNoiseTexture: null,
    uSpecularNoiseTexture: null,
    uSpecularMovementLeftNoiseTexture: null,
    uSpecularMovementRightNoiseTexture: null,
    uFoamTexture: null,
    
    uCausticColor: [0.455, 0.773, 0.765, 1.0],
    uCausticHighlightColor: [0.741, 0.894, 0.898, 1.0],
    uSpecularColor: [1.0, 1.0, 1.0, 1.0],
    uFoamColor: [1.0, 1.0, 1.0, 1.0],
    uOutlineColor: [0.675, 0.86, 1.0, 1.0],
    
    uCausticScale: 12.0,
    uCausticSpeed: 0.005,
    uCausticMovementAmount: 0.15,
    uCausticFaderMultiplier: 1.45,
    
    uSpecularThreshold: 0.35,
    uSpecularSpeed: 0.025,
    uSpecularScale: 15.0,
    
    uFoamIntensity: 0.2,
    uFoamScale: 15.0,
    
    uGeneralTransparency: 1.0,
    
    time: 0.0
}; 